import { useState, useEffect, useRef, useCallback, useMemo, Suspense } from "react";
import { toSnakeCase } from "@/lib/snake-case";
import { canManageApiKeys } from "@/lib/api-keys";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useIsMobile } from "@/hooks/use-mobile";
import { type LucideIcon } from "lucide-react";
import { useIncomingCalls } from "@/hooks/useIncomingCalls";
import { useNotificationSound } from "@/hooks/useNotificationSound";
import { IncomingCallModal } from "@/components/IncomingCallModal";
import { DelayedSpinner } from "@/components/DelayedSpinner";
import { RouteHold } from "@/components/RouteHold";
import { MOTION, prefersReducedMotion } from "@/lib/motion";
import { displayNameOrHandle } from "@/lib/display-name";
import { isBillingLockedForViewer } from "@/lib/agency-billing";
import { AppTopBar } from "@/components/app-shell/AppTopBar";
import { SuiteNav, type Suite, type SuiteGroup, type SuiteItem, type SuiteLink, type SuiteNavEntry } from "@/components/app-shell/SuiteNav";
import { AppMain, AppMobileBar, AppSidebar } from "@/components/app-shell/AppShell";
import { useRailCollapsed } from "@/hooks/useRailCollapsed";
import {
  MessageSquare, Users, Megaphone, Zap, Bot, Phone, BarChart3, PhoneCall, Settings, Library,
  ChevronLeft, ChevronRight, LogOut, LayoutDashboard, Newspaper,
  ChevronDown, Hash, Tag, Clock, Bell, Puzzle, User, UserRound, UsersRound, Shield, ShieldCheck,
  UserCheck, Circle, Inbox, Plus, Moon, Sun, Building2, Check, HelpCircle, Search, LifeBuoy,
  CalendarDays, CalendarClock, ClipboardCheck, Armchair, Code2, Copy, Package, Store,
  BookOpen, Sparkles, FileText, Activity, FileSpreadsheet, SlidersHorizontal, Layers, Bug, Sticker, ShieldBan,
  KeyRound, Compass, AlertTriangle, ClipboardList, Download,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { AssistantCopilotWidget } from "@/components/knowledge-assistant/AssistantCopilotWidget";
import { ASSISTANT_PRODUCT_NAME } from "@/components/knowledge-assistant/product";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useWorkspaceAdminEmails } from "@/hooks/useWorkspaceAdminEmails";
import { useRetellAccount } from "@/voice-assistants/hooks/use-retell-account";
import { useSupportUnreadReplies } from "@/hooks/useSupportRealtime";
import { useCoordinatorUnreadCount } from "@/hooks/useCoordinatorUnreadCount";
import { useCoordinatorStatus } from "@/hooks/useIsCoordinator";
import { useCoordinatorInbox } from "@/hooks/useCoordinatorInbox";
import type { NavGroupItem, NavGroupSection } from "@/components/app-shell/RailNav";
import { usePermissions } from "@/hooks/usePermissions";
import { VoiceAgentIcon } from "@/components/icons/VoiceAgentIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useBrand } from "@/contexts/BrandContext";
import { GracePeriodBanner } from "@/components/GracePeriodBanner";
import { AnnouncementGate, NewsButton } from "@/components/announcements/AnnouncementCenter";
import { AccountStandingBanner } from "@/components/AccountStandingBanner";
import { CardAuthorizationBanner } from "@/components/CardAuthorizationBanner";
import { useAccountStanding } from "@/contexts/AccountStandingContext";
import { CreditBalanceAlertBanner } from "@/components/CreditBalanceAlertBanner";
import { CreditLimitBanner } from "@/components/CreditLimitBanner";
import { ConversationLimitPaywall } from "@/components/ConversationLimitPaywall";
import { ChannelTokenExpiredBanner } from "@/components/ChannelTokenExpiredBanner";
import { VoicePaymentBanner } from "@/components/VoicePaymentBanner";
import { WhatsAppBillingBanners } from "@/components/WhatsAppBillingBlockBanner";
import { UsageCard } from "@/components/UsageCard";
import { useNextWorkspaceFreeQuota } from "@/hooks/useNextWorkspaceFreeQuota";
import { noFreeQuotaWarning } from "@/lib/free-quota";
import { useGuidedTour } from "@/hooks/useGuidedTour";
import { useGuidedTourSwitch } from "@/hooks/useGuidedTourSwitch";
import { useHasRestaurantTables } from "@/hooks/useHasRestaurantTables";
import { useCopilotHome } from "@/hooks/useCopilotHome";
import { COPILOT_HOME_PATH, homeNavVisibility } from "@/lib/copilot-home";
import { WelcomeOverlay } from "@/components/onboarding/WelcomeOverlay";
import { GuidedTour, TourCompletion } from "@/components/onboarding/GuidedTour";
import { GlobalGuideUpdatesNotifier } from "@/components/GlobalGuideUpdatesNotifier";
import { CoordinatorMessageNotifier } from "@/components/CoordinatorMessageNotifier";

const NO_RESPONSE_PATH = "/app/conversations?filter=no_response";

type NavItem = { id: string; label: string; icon: LucideIcon; path: string; permKey: string; adminOnly?: boolean; orPermKey?: string; flowOnly?: boolean; guiaOnly?: boolean };
const navItems: NavItem[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/app/dashboard", permKey: "dashboard" },
];
const inicioItem: NavItem = { id: "inicio", label: "Inicio", icon: Sparkles, path: COPILOT_HOME_PATH, permKey: "" };

// NEGOCIO va primero porque dejó de ser configuración inicial: desde que los
// horarios, festivos, sedes y catálogo salieron del prompt, el runtime lee estas
// dos pantallas EN VIVO en cada mensaje. Son el contenido que responde.
const negocioItems: NavItem[] = [
  { id: "business", label: "Mi negocio", icon: Store, path: "/app/business", permKey: "automations", adminOnly: true, orPermKey: "reservations" },
  { id: "catalog", label: "Catálogo", icon: Package, path: "/app/catalog", permKey: "automations", adminOnly: true },
  { id: "updates", label: "Eventualidades", icon: Newspaper, path: "/app/updates", permKey: "updates" },
];

// AUTOMATIZAR. "Agentes" y "Automatizaciones" son dos accesos a la misma página:
// la primera abre la pestaña de agentes y la segunda la de reglas. Se separan en
// el sidebar porque son dos trabajos distintos, no porque sean dos pantallas.
const automatizarItems: NavItem[] = [
  { id: "automations", label: "Asistentes IA", icon: Bot, path: "/app/automations", permKey: "automations" },
  { id: "rules", label: "Automatizaciones", icon: Zap, path: "/app/automations?tab=keywords", permKey: "automations" },
  { id: "knowledge-bases", label: "Bases de conocimiento", icon: Library, path: "/app/knowledge-bases", permKey: "knowledge_bases" },
  { id: "whatsapp-templates", label: "Plantillas de WhatsApp", icon: FileText, path: "/app/whatsapp-templates", permKey: "whatsapp_templates" },
  { id: "whatsapp-flows", label: "Formularios de WhatsApp", icon: ClipboardList, path: "/app/whatsapp-flows", permKey: "whatsapp_templates" },
  { id: "assistant", label: ASSISTANT_PRODUCT_NAME, icon: Sparkles, path: "/app/assistant", permKey: "knowledge_assistants.consult", guiaOnly: true },
  { id: "knowledge-assistants", label: `${ASSISTANT_PRODUCT_NAME} (matriz)`, icon: BookOpen, path: "/app/knowledge-assistants", permKey: "knowledge_assistants.manage", guiaOnly: true },
  { id: "flow-help", label: "Conoce Flow", icon: Compass, path: "/app/flow-help", permKey: "platform_assistant.consult", guiaOnly: true },
];

// Las pestañas de /app/automations que pertenecen a "Automatizaciones"; el resto
// (y la ausencia de ?tab) es "Agentes".
const RULE_TABS = ["keywords", "rules", "canned", "attribute-reminders"];

// "Campañas" is a collapsible group: text campaigns (/app/campaigns) and voice
// campaigns (/app/voice-assistants/campaigns). Voice additionally requires the
// voice module (see hasVoiceFullAccess).
const campaignsSubItems = [
  { id: "text", label: "Texto", icon: MessageSquare, path: "/app/campaigns", permKey: "campaigns.text" },
  { id: "blueprints", label: "Recurrentes", icon: FileSpreadsheet, path: "/app/campaign-blueprints", permKey: "campaigns.blueprints" },
  { id: "voice", label: "Voz IA", icon: PhoneCall, path: "/app/voice-assistants/campaigns", permKey: "campaigns.voice" },
];

// "Contactos" is a collapsible group: the unified list plus origin filters
// (all / text / voice), driven by the ?origin= query param on /app/contacts.
const contactsSubItems = [
  { id: "all", label: "Todos", icon: Users, path: "/app/contacts", permKey: "contacts" },
  { id: "text", label: "Texto", icon: MessageSquare, path: "/app/contacts?origin=text", permKey: "contacts" },
  { id: "voice", label: "Voz IA", icon: PhoneCall, path: "/app/contacts?origin=voice", permKey: "contacts" },
];

// "Reportes" is a collapsible group: "Reportes Chat" (the operational chat dashboard)
// and "Reportes Voz" (voice-assistant statistics). Both live under the `reports`
// permission; the voice sub-item additionally requires the voice module (see
// hasVoiceStatsAccess).
const reportsSubItems = [
  { id: "reports-chat", label: "Reportes Chat", icon: MessageSquare, path: "/app/reports", permKey: "reports.text" },
  { id: "reports-voice", label: "Reportes Voz", icon: PhoneCall, path: "/app/reports/voice", permKey: "reports.voice" },
  { id: "reports-reservations", label: "Reportes de reservas", icon: CalendarDays, path: "/app/reservations/reports", permKey: "reservations" },
  { id: "reports-quality", label: "Calidad IA", icon: BarChart3, path: "/app/reports/quality", permKey: "reports.text" },
  { id: "reports-presence", label: "Presencia", icon: Activity, path: "/app/reports/presence", permKey: "reports.text" },
  { id: "reports-chat-variables", label: "Variables chat", icon: Code2, path: "/app/reports/chat-variables", permKey: "reports.chat_variables" },
  { id: "reports-whatsapp-forms", label: "Respuestas de formularios", icon: ClipboardCheck, path: "/app/reports/whatsapp-forms", permKey: "reports.whatsapp_forms" },
  // Estos dos llegaron con DE-661 y su migración repartió el permiso a los
  // roles existentes, así que aparecieron solos en las cuentas.
  // Quedan fuera del menú hasta que se decida a quién se le habilitan.
  // { id: "reports-custom-consult", label: "Consultar personalizados", icon: FileSpreadsheet, path: "/app/reports/custom", permKey: "reports.custom_view" },
  // { id: "reports-custom-create", label: "Crear personalizados", icon: SlidersHorizontal, path: "/app/reports/custom/create", permKey: "reports.custom_create" },
  { id: "reports-close-tags", label: "Etiquetas de cierre", icon: Tag, path: "/app/reports/close-tags", permKey: "reports.close_tags" },
  { id: "reports-queues", label: "Colas", icon: Layers, path: "/app/reports/queues", permKey: "reports.queues" },
  { id: "reports-debugger", label: "Debugger", icon: Bug, path: "/app/reports/debugger", permKey: "reports.debugger" },
  { id: "reports-blueprints", label: "Campañas recurrentes", icon: Megaphone, path: "/app/reports/campaign-blueprints", permKey: "campaigns.blueprints.reports" },
];

const conversationSubItems = [
  { id: "all", label: "Todas las conversaciones", icon: MessageSquare, path: "/app/conversations", permKey: "conversations.all" },
  { id: "needs_human", label: "Necesitan humano", icon: UserCheck, path: "/app/conversations?filter=needs_human", permKey: "conversations.needs_human" },
  { id: "no_response", label: "Sin respuesta", icon: MessageSquare, path: NO_RESPONSE_PATH, permKey: "conversations.no_response" },
  { id: "closing_soon", label: "Próximas a cerrarse", icon: Clock, path: "/app/conversations?filter=closing_soon", permKey: "conversations.closing_soon" },
];

// "Agentes y equipos" agrupa a las personas del equipo. Vive en GESTIONAR junto
// team: agents, teams and the attention queues that route to them.
// a Colas y Roles. El encabezado del grupo ya lo distingue de los asistentes IA.
const agentSubItems: Array<{ id: string; label: string; icon: typeof Users; path: string; permKey?: string }> = [
  { id: "team", label: "Agentes", icon: Users, path: "/app/settings/team", permKey: "settings.team" },
  { id: "coord-team", label: "Gestión de coordinadores", icon: UsersRound, path: "/app/coordinator/team", permKey: "settings.team" },
  { id: "coord-chat", label: "Chat coordinador", icon: MessageSquare, path: "/app/coordinator/chat" },
];

// Hermanos de "Agentes y equipos" en GESTIONAR: no cuelgan de él porque no son
// personas, son cómo se reparte y quién puede qué.
const gestionarItems: Array<{ id: string; label: string; icon: typeof Users; path: string; permKey: string }> = [
  { id: "queues", label: "Colas", icon: Inbox, path: "/app/settings/queues", permKey: "settings.queues" },
  { id: "roles", label: "Roles", icon: ShieldCheck, path: "/app/settings/roles", permKey: "settings.roles" },
];

// CONFIGURAR: las mismas páginas de siempre, agrupadas por el trabajo que
// resuelven en vez de listadas planas. Roles salió de acá a GESTIONAR.
type SettingsGroup = { id: string; label: string; icon: typeof Users; items: Array<{ id: string; label: string; icon: typeof Users; path: string; permKey: string }> };
const settingsGroups: SettingsGroup[] = [
  { id: "canales", label: "Canales e integraciones", icon: Hash, items: [
    { id: "channels", label: "Canales", icon: Hash, path: "/app/settings/channels", permKey: "settings.channels" },
    { id: "integrations", label: "Integraciones", icon: Puzzle, path: "/app/settings/integrations", permKey: "settings.integrations" },
    { id: "telephony", label: "Telefonía", icon: Phone, path: "/app/voice-assistants/telephony", permKey: "settings.telephony" },
  ] },
  { id: "datos", label: "Datos y etiquetas", icon: Tag, items: [
    { id: "labels", label: "Etiquetas", icon: Tag, path: "/app/settings/labels", permKey: "settings.labels" },
    { id: "close-tags", label: "Etiquetas cierre", icon: Tag, path: "/app/settings/close-tags", permKey: "close_tags.manage" },
    { id: "stickers", label: "Stickers", icon: Sticker, path: "/app/settings/stickers", permKey: "settings.stickers" },
    { id: "blacklists", label: "Listas negras", icon: ShieldBan, path: "/app/settings/blacklists", permKey: "settings.blacklists" },
    { id: "custom-attributes", label: "Atributos personalizados", icon: Code2, path: "/app/settings/custom-attributes", permKey: "settings.custom_attributes" },
    { id: "extraction-variables", label: "Variables extracción", icon: Code2, path: "/app/settings/extraction-variables", permKey: "chat_extraction.manage" },
  ] },
  { id: "cuenta", label: "Cuenta y preferencias", icon: User, items: [
    { id: "account", label: "Plan y facturación", icon: User, path: "/app/settings/account", permKey: "settings.account" },
    { id: "notifications", label: "Notificaciones", icon: Bell, path: "/app/settings/notifications", permKey: "settings.notifications" },
    { id: "auto-close", label: "Cierre automático", icon: Clock, path: "/app/settings/auto-close", permKey: "settings.auto_close" },
    { id: "workspace-rules", label: "Reglas del workspace", icon: SlidersHorizontal, path: "/app/settings/workspace-rules", permKey: "settings.auto_close" },
    { id: "agent-statuses", label: "Estados", icon: Circle, path: "/app/settings/agent-statuses", permKey: "settings.agent_statuses" },
    { id: "matriz", label: "Matriz", icon: Building2, path: "/app/settings/matriz", permKey: "settings.matriz" },
    { id: "security", label: "Tu perfil", icon: UserRound, path: "/app/settings/security", permKey: "settings.account" },
    { id: "api-access", label: "API", icon: Code2, path: "/app/settings/api-access", permKey: "settings.account" },
    { id: "credentials", label: "Credenciales", icon: KeyRound, path: "/app/settings/credentials", permKey: "settings.account" },
    { id: "data-export", label: "Descargar datos", icon: Download, path: "/app/settings/data-export", permKey: "settings.account" },
  ] },
];
const settingsSubItems = settingsGroups.flatMap((g) => g.items);
const CHANNEL_SETTING_IDS = ["channels", "integrations", "telephony", "api-access", "credentials"];

const reservationSubItems = [
  { id: "confirmation", label: "Confirmación de reservas", icon: ClipboardCheck, path: "/app/reservations/confirmation" },
  { id: "floor", label: "Asistencia de reservas", icon: Armchair, path: "/app/reservations/floor" },
  { id: "calendar", label: "Calendario de reservas", icon: CalendarDays, path: "/app/reservations/calendar" },
  { id: "incomplete", label: "Reservas incompletas", icon: CalendarClock, path: "/app/reservations/incomplete" },
];

// Routes that compose their own islands over the app background instead of
// taking one white sheet from the layout. The sheet stretches to the full
// viewport, so any page whose content stops short trails a large empty white
// block underneath it; a page listed here paints only where it has content.
// Migrating a page means dropping the background colour from its root and
// giving a surface to whatever was relying on the sheet for one.
const ISLAND_COMPOSED_EXACT = [
  "/app/dashboard",
  "/app/automations",
  "/app/support",
  "/app/updates",
  "/app/knowledge-assistants",
  "/app/reservations/incomplete",
];
const ISLAND_COMPOSED_PREFIXES = ["/app/reports", "/app/voice-assistants", "/app/help"];
const ISLAND_COMPOSED_PATTERNS = [
  /\/voice-assistants\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/,
  /\/agents\/[^/]+\/nuevo-editor/,
];

/** Screens that fill the panel edge to edge: their own two-pane frame is the
 *  card, so the sheet's margin would only draw a gutter around it. */
const FULL_BLEED_EXACT = ["/app/conversations"];

function isFullBleed(pathname: string) {
  return FULL_BLEED_EXACT.includes(pathname);
}

function composesOwnIslands(pathname: string) {
  return ISLAND_COMPOSED_EXACT.includes(pathname)
    || ISLAND_COMPOSED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
    || ISLAND_COMPOSED_PATTERNS.some((re) => re.test(pathname));
}

const statusColors: Record<string, string> = {
  online: "bg-flow-green",
  offline: "bg-muted-foreground/40",
  busy: "bg-flow-orange",
  logged_in: "bg-blue-500",
};

/** The title the content header shows. Derived from the rail's own config by
 *  longest matching path, so a destination names itself in one place instead of
 *  every page repeating its own heading. */
const TITLE_SOURCES = [
  inicioItem, ...navItems, ...negocioItems, ...automatizarItems,
  ...campaignsSubItems, ...contactsSubItems, ...reportsSubItems,
  ...conversationSubItems, ...reservationSubItems,
];
function titleForPath(pathname: string): string {
  let best = "";
  let label = "";
  for (const item of TITLE_SOURCES) {
    const base = item.path.split("?")[0];
    if (pathname.startsWith(base) && base.length > best.length) {
      best = base;
      label = item.label;
    }
  }
  return label;
}

type SelectableAgentStatus = {
  id: string;
  key: string;
  name: string;
  color: string;
  is_system: boolean;
  assigned: boolean;
};

export function AppLayout() {
  const brand = useBrand();
  const isMobile = useIsMobile();
  // Open by default: the kit's sidebar is a 260px column with named groups, and
  // an icon rail cannot show them. The toggle stays for anyone who wants the
  // width back, and the choice is still remembered.
  const [desktopCollapsed, setDesktopCollapsed] = useRailCollapsed();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Desktop sidebar opens/closes only via the toggle button (no hover-to-expand).
  // On mobile it is a hamburger-triggered off-canvas drawer.
  const collapsed = isMobile ? false : desktopCollapsed;
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [creatingWs, setCreatingWs] = useState(false);
  const nextWsFreeQuota = useNextWorkspaceFreeQuota(newWsOpen);
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    if (prefersReducedMotion() || !contentRef.current) return;
    const animate = () => {
      const root = contentRef.current?.firstElementChild;
      if (!root) return;
      // Page roots often carry Tailwind's transition-* for hover/theme polish —
      // that CSS transition fights gsap writing opacity/transform inline every
      // frame and can freeze the tween mid-flight. Neutralize for the fade,
      // restore on completion so the page's own transitions keep working.
      gsap.set(root, { transition: "none" });
      gsap.from(root, {
        y: MOTION.pageRise, opacity: 0, duration: MOTION.duration, ease: MOTION.ease,
        onComplete() { gsap.set(root, { clearProps: "transition" }); },
      });
    };
    // Outlet content mounts async (Suspense/lazy chunk) — a mutation observer
    // catches the fallback→real swap, since a pathname-only re-run misses it
    // on a hard refresh (chunk resolves after this effect already ran).
    const observer = new MutationObserver(animate);
    observer.observe(contentRef.current, { childList: true });
    animate();
    return () => {
      observer.disconnect();
    };
  }, { scope: contentRef, dependencies: [location.pathname], revertOnUpdate: true });
  const isIslandComposedRoute = composesOwnIslands(location.pathname);
  const fullBleedRoute = isFullBleed(location.pathname);
  const { agent, user, signOut, updateAgentStatus, organizationName, workspaces, activeWorkspaceId, switchWorkspace, userRole, billingManagedByAgency, isMatrixDelegate } = useAuth();
  const guiaEnabled = workspaces.find(w => w.organization_id === activeWorkspaceId)?.guia_enabled ?? false;
  const [guideUnreadCount, setGuideUnreadCount] = useState(0);
  const [agentStatuses, setAgentStatuses] = useState<SelectableAgentStatus[]>([]);
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();

  const loadAgentStatuses = useCallback(async () => {
    if (!activeWorkspaceId) {
      setAgentStatuses([]);
      return;
    }
    const { data, error } = await supabase.rpc("list_agent_statuses_for_workspace" as never, {
      p_workspace_id: activeWorkspaceId,
    } as never);
    if (error) {
      console.error("Error loading agent statuses:", error);
      setAgentStatuses([]);
      return;
    }
    const rows = (data as unknown as SelectableAgentStatus[]) || [];
    setAgentStatuses(rows.filter((s) => s.is_system || s.assigned));
  }, [activeWorkspaceId]);

  useEffect(() => {
    void loadAgentStatuses();
  }, [loadAgentStatuses]);

  const handleSetAgentStatus = async (statusId: string) => {
    const result = await updateAgentStatus(statusId);
    if (!result.ok) {
      toast.error(result.message || "No se pudo cambiar el estado");
      return;
    }
    const selected = agentStatuses.find((s) => s.id === statusId);
    if (selected) toast.success(`Estado: ${selected.name}`);
  };

  const currentAgentStatus =
    agentStatuses.find((s) => s.id === agent?.current_status_id)
    || agentStatuses.find((s) => s.is_system && s.key === agent?.availability_status)
    || null;

  const statusDotClass = currentAgentStatus
    ? (statusColors[currentAgentStatus.key] || "")
    : statusColors[agent?.availability_status || "offline"];
  const statusDotStyle = currentAgentStatus && !statusColors[currentAgentStatus.key]
    ? { backgroundColor: currentAgentStatus.color }
    : undefined;
  const adminEmailsByOrg = useWorkspaceAdminEmails(isSuperAdmin);
  const { hasAccount: hasVoiceAccess, loading: voiceAccessLoading } = useRetellAccount();
  const { can } = usePermissions();
  const unreadSupport = useSupportUnreadReplies();
  const supportUnread = unreadSupport.length;
  const coordUnread = useCoordinatorUnreadCount();
  const { isCoordinator, hasCoordinator } = useCoordinatorStatus();
  const { teamAgents, ownInboxIsMine } = useCoordinatorInbox();
  const conversationSections = useMemo<NavGroupSection[]>(() => {
    const own = conversationSubItems.filter((sub) => can(sub.permKey));
    if (teamAgents.length === 0) return [{ id: "conversations", items: own }];
    const mine = { id: "mine", label: "Mis conversaciones", icon: UserRound, path: "/app/conversations?scope=mine" };
    const ownItems = ownInboxIsMine
      ? own.map((sub) => (sub.id === "all" ? { ...sub, label: "Mis conversaciones", icon: UserRound } : sub))
      : own.flatMap((sub) => (sub.id === "all" ? [sub, mine] : [sub]));
    return [
      { id: "conversations", items: ownItems },
      {
        id: "team",
        label: "Mi equipo",
        items: [
          { id: "team-all", label: "Todo el equipo", icon: UsersRound, path: "/app/conversations?team=all" },
          ...teamAgents.map((a) => ({ id: `team-${a.agentId}`, label: a.fullName, icon: User, path: `/app/conversations?team=${a.agentId}` })),
        ],
      },
    ];
  }, [can, teamAgents, ownInboxIsMine]);
  const isAdmin = userRole === "admin" || isSuperAdmin;
  const billingLocked = isBillingLockedForViewer({
    billingManagedByAgency,
    isMatrixDelegate,
    isSuperAdmin: !superAdminLoading && isSuperAdmin,
  });
  const standing = useAccountStanding();
  const standingBannerShown = !billingLocked && (standing?.kind === "grace" || standing?.kind === "paused");
  // Voice module: full access for super-admins + orgs with a Motor de Voz account; a
  // locked "Próximamente" teaser for orgs without one (only once access is resolved).
  // The account gate is ANDed with the RBAC key so org admins can hide the module per
  // role; when the role denies it, nothing renders (not even the teaser).
  const voiceAllowedByRole = can("voice_assistants");
  const hasVoiceFullAccess = (isSuperAdmin || hasVoiceAccess) && voiceAllowedByRole;
  // "Reportes Voz" lives inside the Reportes group (already gated by `reports`), so it
  // only needs the voice module gate on top: it shows for orgs that actually have the
  // Motor de Voz module.
  const hasVoiceStatsAccess = hasVoiceFullAccess;
  const voiceAccessKnown = !superAdminLoading && !voiceAccessLoading;
  const { incomingCall, callStatus, callQueueCount, acceptCall, rejectCall, endCall, dismissCall, initiateCall } = useIncomingCalls();
  useNotificationSound(null);
  const { theme, setTheme } = useTheme();
  const guidedTour = useGuidedTourSwitch();
  const tour = useGuidedTour(guidedTour.enabled);
  const hasRestaurantTables = useHasRestaurantTables();
  const copilotHome = useCopilotHome();
  const homeNav = homeNavVisibility(copilotHome.enabled, can("dashboard") && !copilotHome.loading);
  const isVoiceCampaignsActive = location.pathname.startsWith("/app/voice-assistants/campaigns");
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  // The identity row has to render something, so it falls back to the handle
  // where the greeting would rather say nothing.
  const accountName = displayNameOrHandle({
    agentFullName: agent?.full_name,
    metadataFullName: user?.user_metadata?.full_name as string | undefined,
    email: agent?.email ?? user?.email,
  });

  const initials = accountName
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "??";

  const mobileNav = (to: string) => {
    navigate(to);
    setMobileMenuOpen(false);
  };

  const handleCreateWorkspace = async () => {
    const name = newWsName.trim();
    if (!name) return;

    setCreatingWs(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-workspace", {
        body: { name },
      });

      if (error || !data?.success || !data?.organization?.id) {
        throw new Error(data?.error || error?.message || "Error al crear workspace");
      }

      toast.success(`Workspace "${name}" creado`);
      if (data.free_quota_withheld === true) {
        toast.info("Este workspace no incluye conversaciones gratis. Elige un plan para que la IA responda.");
      }
      setNewWsOpen(false);
      setNewWsName("");
      await switchWorkspace(data.organization.id);
    } catch (err) {
      console.error("Create workspace error:", err);
      toast.error(err instanceof Error ? err.message : "Error al crear workspace");
    } finally {
      setCreatingWs(false);
    }
  };

  const workspaceMenuItems = (
    <>
      {workspaces.map((ws) => {
        const adminEmails = adminEmailsByOrg[ws.organization_id];
        return (
          <DropdownMenuItem
            key={ws.organization_id}
            onClick={() => { switchWorkspace(ws.organization_id); setMobileMenuOpen(false); }}
            className="flex items-center gap-2"
          >
            <Building2 className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block truncate">
                {ws.organization_name}
                {ws.workspace_type === "matriz" && (
                  <span className="ml-1.5 text-[10px] font-medium text-violet-500 align-middle">MATRIZ</span>
                )}
                {ws.is_associate && (
                  <span className="ml-1.5 text-[10px] font-medium text-muted-foreground align-middle">ASOCIADO</span>
                )}
              </span>
              {adminEmails && adminEmails.length > 0 && (
                <span className="block truncate text-[10px] leading-tight text-muted-foreground">
                  {adminEmails.join(", ")}
                </span>
              )}
            </span>
            {ws.organization_id === activeWorkspaceId && (
              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
            )}
          </DropdownMenuItem>
        );
      })}
      {isAdmin && (brand.id !== "etb" || isSuperAdmin) && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setNewWsOpen(true)} className="flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" />
            <span>Crear nuevo workspace</span>
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  // The account menu used to sit at the foot of the sidebar; it moved to the top
  // bar rather than being duplicated, so there is still one way into it.
  const accountMenu = (
      <DropdownMenu onOpenChange={(open) => { if (open) void loadAgentStatuses(); }}>
        <DropdownMenuTrigger asChild>
          <button className="flex max-w-[220px] shrink-0 items-center gap-2.5 rounded-full py-1 pl-1 pr-2.5 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]" aria-label="Cuenta y estado">
            <span className="relative shrink-0">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                {initials}
              </span>
              <span
                className={cn("absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-sidebar", statusDotClass || "bg-muted-foreground/40")}
                style={statusDotStyle}
              />
            </span>
            <span className="hidden min-w-0 flex-1 md:block">
              <span className="block truncate text-[13px] font-semibold text-ink-900">{accountName}</span>
              <span className="mt-0.5 block truncate text-[11px] leading-none text-mute">{organizationName}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end" className="z-[80] max-h-[70vh] w-72 overflow-y-auto">
          <div className="px-2 py-2">
            <p className="truncate text-xs font-medium text-foreground">{accountName}</p>
            <p className="truncate text-[11px] text-muted-foreground">{organizationName}</p>
          </div>
          <DropdownMenuSeparator />
          <div className="px-2 py-2">
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">ID de la cuenta</p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (activeWorkspaceId) {
                  navigator.clipboard.writeText(activeWorkspaceId);
                  toast.success("ID copiado al portapapeles");
                }
              }}
              className="group flex w-full items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5 transition-colors hover:bg-muted"
              title="Click para copiar"
            >
              <code className="truncate font-mono text-[11px] text-foreground">{activeWorkspaceId || "\u2014"}</code>
              <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
            </button>
          </div>
          <DropdownMenuSeparator />
          <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Estado</p>
          {agentStatuses.map((st) => (
            <DropdownMenuItem key={st.id} onClick={() => handleSetAgentStatus(st.id)}>
              <span
                className={cn("mr-2 h-2 w-2 rounded-full", statusColors[st.key] || "")}
                style={!statusColors[st.key] ? { backgroundColor: st.color } : undefined}
              />
              {st.name}
              {agent?.current_status_id === st.id && (
                <Check className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
              )}
            </DropdownMenuItem>
          ))}
          {agentStatuses.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">Cargando estados…</div>
          )}
          <DropdownMenuSeparator />
          {isSuperAdmin && (
            <DropdownMenuItem onClick={() => navigate("/super-admin")}>
              <Shield className="mr-2 h-4 w-4" /> Consola SuperAdmin
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={signOut} className="text-destructive">
            <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
  );

  const visibleReportItems = reportsSubItems.filter((sub) => {
    if (sub.id === "reports-reservations") return hasRestaurantTables && can("reservations");
    if (sub.id === "reports-whatsapp-forms") return can(sub.permKey);
    if (!can("reports")) return false;
    return sub.id === "reports-voice" ? (hasVoiceStatsAccess && can("reports.voice")) : can(sub.permKey);
  });

  const negocioVisibles = negocioItems.filter((item) =>
    (can(item.permKey) && (!item.adminOnly || isAdmin))
    || (!!item.orPermKey && hasRestaurantTables && can(item.orPermKey))
  );

  const visibleAgentSubItems = agentSubItems
    .filter((sub) => !sub.permKey || can(sub.permKey))
    .filter((sub) => sub.id !== "coord-chat" || isCoordinator || hasCoordinator)
    .map((sub) => sub.id === "coord-chat" && coordUnread > 0
      ? { ...sub, badge: <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">{coordUnread}</span> }
      : sub,
    );

  const mainNavItems = automatizarItems.filter((item) =>
    can(item.permKey)
    && (!item.adminOnly || isAdmin)
    && (!item.flowOnly || brand.id === "flow")
    && (!item.guiaOnly || guiaEnabled),
  );

  const pathWithSearch = location.pathname + location.search;
  const searchParams = new URLSearchParams(location.search);
  const toSuiteItem = (sub: NavGroupItem, active: boolean): SuiteItem => ({
    id: sub.id, label: sub.label, icon: sub.icon, path: sub.path, badge: sub.badge, active,
  });
  const exactActive = (sub: NavGroupItem) => location.pathname === sub.path;

  const conversationActive = (sub: NavGroupItem) =>
    pathWithSearch === sub.path
    || (sub.id === "all" && location.pathname.startsWith("/app/conversations") && !location.search);
  const ownConversationItems = conversationSections.find((section) => section.id === "conversations")?.items ?? [];
  const teamConversationItems = conversationSections.find((section) => section.id === "team")?.items ?? [];
  const coordinatorChatItem = visibleAgentSubItems.find((sub) => sub.id === "coord-chat");
  const conversationsSuite: Suite = {
    id: "conversations",
    label: "Conversaciones",
    icon: MessageSquare,
    dataTour: "conversations",
    routePrefixes: ["/app/conversations", "/app/coordinator"],
    groups: [
      { id: "inbox", items: (can("conversations") ? ownConversationItems.filter((sub) => sub.id === "all" || sub.id === "mine") : []).map((sub) => toSuiteItem(sub, conversationActive(sub))) },
      { id: "filters", label: "Filtros", items: (can("conversations") ? ownConversationItems.filter((sub) => sub.id !== "all" && sub.id !== "mine") : []).map((sub) => toSuiteItem(sub, conversationActive(sub))) },
      {
        id: "team",
        label: "Equipo",
        items: [
          ...(can("conversations") ? teamConversationItems.map((sub) => toSuiteItem(sub, conversationActive(sub))) : []),
          ...(coordinatorChatItem ? [toSuiteItem(coordinatorChatItem, exactActive(coordinatorChatItem))] : []),
        ],
      },
    ],
  };

  const onAutomations = location.pathname.startsWith("/app/automations");
  const automationsTab = searchParams.get("tab") ?? "";
  const automationItemActive = (item: NavItem) =>
    item.id === "automations"
      ? onAutomations && !RULE_TABS.includes(automationsTab)
      : item.id === "rules"
        ? onAutomations && RULE_TABS.includes(automationsTab)
        : location.pathname.startsWith(item.path);
  const automationGroupItems = (ids: string[]): SuiteItem[] =>
    mainNavItems
      .filter((item) => ids.includes(item.id))
      .map((item) => ({
        id: item.id,
        label: item.label,
        icon: item.icon,
        path: item.path,
        active: automationItemActive(item),
        badge: item.id === "assistant" && guideUnreadCount > 0 ? (
          <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
            {guideUnreadCount}
          </span>
        ) : undefined,
      }));
  const assistantsSuite: Suite = {
    id: "assistants",
    label: "Asistentes IA",
    icon: Bot,
    dataTour: "automations",
    routePrefixes: ["/app/automations", "/app/knowledge-bases", "/app/whatsapp-templates", "/app/whatsapp-flows", "/app/assistant", "/app/knowledge-assistants", "/app/voice-assistants", "/app/agents"],
    groups: [
      { id: "assistants", label: "Asistentes", items: automationGroupItems(["automations", "rules", "assistant", "knowledge-assistants"]) },
      { id: "knowledge", label: "Conocimiento", items: automationGroupItems(["knowledge-bases"]) },
      { id: "whatsapp", label: "WhatsApp", items: automationGroupItems(["whatsapp-templates", "whatsapp-flows"]) },
    ],
  };

  const campaignItems = (can("campaigns.text") || can("campaigns.blueprints") || (hasVoiceFullAccess && can("campaigns.voice")))
    ? campaignsSubItems
      .filter((sub) => sub.id === "voice" ? (hasVoiceFullAccess && can("campaigns.voice")) : can(sub.permKey))
      .map((sub) => toSuiteItem(sub, sub.id === "text"
        ? location.pathname === "/app/campaigns"
        : sub.id === "blueprints"
          ? location.pathname.startsWith("/app/campaign-blueprints")
          : isVoiceCampaignsActive))
    : [];
  const flowAdsItems: SuiteItem[] = can("automations") && isAdmin && brand.id === "flow"
    ? [{ id: "flow-ads", label: "Flow Ads", icon: Megaphone, path: "/app/ads", active: location.pathname.startsWith("/app/ads") }]
    : [];
  const currentOrigin = searchParams.get("origin");
  const contactItems = can("contacts")
    ? contactsSubItems
      .filter((sub) =>
        sub.id === "all" ? (can("contacts.text") && can("contacts.voice"))
          : sub.id === "text" ? can("contacts.text")
            : can("contacts.voice"))
      .map((sub) => toSuiteItem(sub, location.pathname.startsWith("/app/contacts")
        && (sub.id === "all" ? !currentOrigin : currentOrigin === sub.id)))
    : [];
  const campaignsSuite: Suite = {
    id: "campaigns",
    label: "Campañas y contactos",
    icon: Megaphone,
    dataTour: "campaigns",
    routePrefixes: ["/app/campaigns", "/app/campaign-blueprints", "/app/contacts", "/app/ads", "/app/voice-assistants/campaigns"],
    groups: [
      { id: "campaigns", label: "Campañas", items: [...campaignItems, ...flowAdsItems] },
      { id: "contacts", label: "Contactos", items: contactItems },
    ],
  };

  const businessSuite: Suite = {
    id: "business",
    label: "Mi negocio",
    icon: Store,
    routePrefixes: ["/app/business", "/app/catalog", "/app/updates", "/app/reservations"],
    groups: [
      {
        id: "business",
        items: negocioVisibles.map((item) => toSuiteItem(item, location.pathname.startsWith(item.path))),
      },
      {
        id: "reservations",
        label: "Reservas",
        items: hasRestaurantTables && can("reservations")
          ? reservationSubItems.map((sub) => toSuiteItem(sub, exactActive(sub)))
          : [],
      },
    ],
  };

  const reportItemsFor = (ids: string[]): SuiteItem[] =>
    ids.flatMap((id) => {
      const sub = visibleReportItems.find((item) => item.id === id);
      if (!sub) return [];
      return [toSuiteItem(sub, sub.id === "reports-chat" ? location.pathname === "/app/reports" : location.pathname.startsWith(sub.path))];
    });
  const reportsSuite: Suite = {
    id: "reports",
    label: "Reportes",
    icon: BarChart3,
    dataTour: "reports",
    routePrefixes: ["/app/reports", "/app/reservations/reports"],
    groups: [
      { id: "attention", label: "Atención", items: reportItemsFor(["reports-chat", "reports-voice", "reports-quality", "reports-queues", "reports-presence", "reports-close-tags"]) },
      { id: "data", label: "Datos", items: reportItemsFor(["reports-chat-variables", "reports-whatsapp-forms", "reports-reservations", "reports-blueprints"]) },
      { id: "tools", label: "Herramientas", items: reportItemsFor(["reports-debugger"]) },
    ],
  };

  const allowedSettingItems = can("settings")
    ? settingsSubItems.filter((sub) =>
      can(sub.permKey)
      && !(billingLocked && sub.id === "account")
      && (sub.id !== "data-export" || (brand.id === "flow" && userRole === "admin"))
      && (sub.id !== "api-access" || canManageApiKeys(userRole, !superAdminLoading && isSuperAdmin)))
    : [];
  const channelItems = CHANNEL_SETTING_IDS.flatMap((id) => {
    const sub = allowedSettingItems.find((item) => item.id === id);
    return sub ? [toSuiteItem(sub, exactActive(sub))] : [];
  });
  const channelsSuite: Suite = {
    id: "channels",
    label: "Canales e integraciones",
    icon: Hash,
    routePrefixes: ["/app/voice-assistants/telephony"],
    groups: [{ id: "channels", items: channelItems }],
  };

  const settingsGroupsVisible: SuiteGroup[] = settingsGroups.map((group) => ({
    id: group.id,
    label: group.label,
    items: allowedSettingItems
      .filter((sub) => group.items.some((item) => item.id === sub.id) && !CHANNEL_SETTING_IDS.includes(sub.id))
      .map((sub) => toSuiteItem(sub, exactActive(sub))),
  }));
  const teamSettingItems = [
    ...visibleAgentSubItems.filter((sub) => sub.id !== "coord-chat"),
    ...gestionarItems.filter((it) => can(it.permKey)),
  ].map((sub) => toSuiteItem(sub, exactActive(sub)));
  const helpItems: SuiteItem[] = [
    ...(can("help") ? [{ id: "help", label: "Centro de Ayuda", icon: HelpCircle, path: "/app/help", active: location.pathname === "/app/help" }] : []),
    ...(can("support") ? [{
      id: "support",
      label: "Soporte",
      icon: LifeBuoy,
      path: "/app/support",
      active: location.pathname === "/app/support",
      badge: supportUnread > 0 ? (
        <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
          {supportUnread}
        </span>
      ) : undefined,
    }] : []),
  ];
  const settingsGroupList: SuiteGroup[] = [
    ...settingsGroupsVisible,
    { id: "team", label: "Equipo y accesos", items: teamSettingItems },
    { id: "help", label: "Ayuda", items: helpItems },
  ];
  const settingsLanding = settingsGroupList.flatMap((group) => group.items)[0]?.path;
  const settingsSuite: Suite = {
    id: "settings",
    label: "Configuración",
    icon: Settings,
    dataTour: "settings",
    routePrefixes: ["/app/settings", "/app/help", "/app/support"],
    landingPath: settingsLanding,
    groups: settingsGroupList,
  };

  const homeLink: SuiteLink | null = homeNav.showInicio
    ? { id: inicioItem.id, label: inicioItem.label, icon: inicioItem.icon, path: inicioItem.path, active: location.pathname === inicioItem.path, dataTour: inicioItem.id }
    : homeNav.showDashboard
      ? { id: navItems[0].id, label: navItems[0].label, icon: navItems[0].icon, path: navItems[0].path, active: location.pathname.startsWith(navItems[0].path), dataTour: navItems[0].id }
      : null;
  const flowHelpItem = mainNavItems.find((item) => item.id === "flow-help");
  const suiteEntries: SuiteNavEntry[] = [
    ...(homeLink ? [{ kind: "link" as const, link: homeLink }] : []),
    { kind: "divider", id: "divider-top" },
    { kind: "suite", suite: conversationsSuite },
    { kind: "suite", suite: assistantsSuite },
    { kind: "suite", suite: campaignsSuite },
    { kind: "suite", suite: businessSuite },
    { kind: "suite", suite: reportsSuite },
    { kind: "suite", suite: channelsSuite },
    { kind: "divider", id: "divider-bottom" },
    { kind: "suite", suite: settingsSuite },
    ...(flowHelpItem ? [{
      kind: "link" as const,
      link: { id: flowHelpItem.id, label: "Pregúntale a Flow", icon: flowHelpItem.icon, path: flowHelpItem.path, active: location.pathname.startsWith(flowHelpItem.path) },
    }] : []),
  ];
  return (
    <div className="app-gradient-bg flex h-[100dvh] w-full flex-col overflow-hidden">
      {!isMobile && (
        <AppTopBar
          logoSrc={brand.logos.sidebar}
          iconSrc={brand.logos.icon}
          logoAlt={brand.shortName}
          leadingWidth={collapsed ? "w-14" : "w-[252px]"}
          searchActive={location.pathname === "/app/search"}
          searchShortcut="/"
          onSearch={() => navigate("/app/search")}
          actions={<><NewsButton />{accountMenu}</>}
          railCollapsed={collapsed}
          onToggleRail={() => setDesktopCollapsed(!desktopCollapsed)}
        />
      )}
      {isMobile && (
        <AppMobileBar
          logoSrc={brand.logos.sidebar}
          logoAlt={brand.shortName}
          onOpenMenu={() => setMobileMenuOpen(true)}
          account={(
            <div className="flex items-center gap-1">
            <NewsButton className="h-8 w-8" />
            <DropdownMenu onOpenChange={(open) => { if (open) void loadAgentStatuses(); }}>
              <DropdownMenuTrigger asChild>
                <button className="relative">
                  <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-[10px] font-semibold text-primary-foreground">
                    {initials}
                  </div>
                  <div
                    className={cn("absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-sidebar", statusDotClass || "bg-muted-foreground/40")}
                    style={statusDotStyle}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="end" className="w-56 max-h-[60vh] overflow-y-auto">
                {agentStatuses.map((s) => (
                  <DropdownMenuItem key={s.id} onClick={() => handleSetAgentStatus(s.id)}>
                    <div
                      className={cn("h-2 w-2 rounded-full mr-2", statusColors[s.key] || "")}
                      style={!statusColors[s.key] ? { backgroundColor: s.color } : undefined}
                    />
                    {s.name}
                    {agent?.current_status_id === s.id && (
                      <Check className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
                    )}
                  </DropdownMenuItem>
                ))}
                {agentStatuses.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">Cargando estados…</div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTheme(theme === "dark" ? "light" : "dark"); }}>
                  {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                  {theme === "dark" ? "Modo claro" : "Modo oscuro"}
                </DropdownMenuItem>
                {isSuperAdmin && (
                  <DropdownMenuItem onClick={() => mobileNav("/super-admin")}>
                    <Shield className="h-4 w-4 mr-2" /> Consola SuperAdmin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={signOut} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          )}
        />
      )}

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
      <AppSidebar
        isMobile={isMobile}
        collapsed={collapsed}
        footer={!collapsed && !billingLocked ? (
          <UsageCard showWhatsApp={isAdmin} showVoice={hasVoiceFullAccess} showPlan allowPlanChange={brand.id !== "etb"} showCompensations={brand.id === "etb"} />
        ) : undefined}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        onExpand={() => setDesktopCollapsed(false)}
        logoSrc={brand.logos.sidebar}
        logoAlt={brand.shortName}
        head={!isMobile && workspaces.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                "flex w-full min-w-0 items-center gap-2.5 rounded-lg bg-panel shadow-[0_1px_3px_rgb(15_23_42/0.08)] transition-shadow hover:shadow-[0_1px_4px_rgb(15_23_42/0.14)]",
                collapsed ? "justify-center p-1.5" : "px-2.5 py-1.5",
              )}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[15px] font-bold uppercase text-primary">
                  {(organizationName || "W").trim().charAt(0)}
                </span>
                {!collapsed && (
                  <>
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block truncate text-[13px] font-semibold leading-tight text-ink-900">{organizationName || "Workspace"}</span>
                      <span className="block truncate text-[11px] leading-tight text-mute">{isAdmin ? "Administrador" : "Agente"}</span>
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-mute" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-[80] max-h-[60vh] w-[248px] overflow-y-auto">
              {workspaceMenuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      >
          <SuiteNav entries={suiteEntries} collapsed={collapsed} pathname={location.pathname} onSelect={mobileNav} />
      </AppSidebar>

      <AppMain isMobile={isMobile}>
        {!billingLocked && <AccountStandingBanner isSuperAdmin={isSuperAdmin} unansweredPath={can("conversations.no_response") ? NO_RESPONSE_PATH : null} />}
        {!billingLocked && <CardAuthorizationBanner />}
        <ChannelTokenExpiredBanner />
        <VoicePaymentBanner />
        <WhatsAppBillingBanners />
        {!standingBannerShown && <GracePeriodBanner agencyManaged={billingLocked} />}
        {!billingLocked && <CreditBalanceAlertBanner />}
        {!billingLocked && <CreditLimitBanner />}
        {isIslandComposedRoute ? (
          <div ref={contentRef} className="contents">
            <RouteHold className={isMobile ? "m-2" : "m-3"}>
              <Suspense fallback={<DelayedSpinner />}>
                <Outlet context={{ initiateCall }} />
              </Suspense>
            </RouteHold>
          </div>
        ) : (
          <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", fullBleedRoute ? "" : isMobile ? "m-2" : "m-3")}>
            <div ref={contentRef} className="contents">
              <RouteHold>
                <Suspense fallback={<DelayedSpinner />}>
                  <Outlet context={{ initiateCall }} />
                </Suspense>
              </RouteHold>
            </div>
          </div>
        )}
        <AnnouncementGate />
        <ConversationLimitPaywall organizationId={activeWorkspaceId} isSuperAdmin={isSuperAdmin} agencyManaged={billingLocked} />
      </AppMain>
      </div>

      <audio id="remote-call-audio" autoPlay className="hidden" />

      {incomingCall && (
        <IncomingCallModal
          call={incomingCall}
          callStatus={callStatus}
          queueCount={callQueueCount}
          onAccept={acceptCall}
          onReject={rejectCall}
          onEnd={endCall}
          onDismiss={dismissCall}
        />
      )}

      <Dialog open={newWsOpen} onOpenChange={setNewWsOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Crear nuevo workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Nombre del workspace</Label>
              <Input
                value={newWsName}
                onChange={(e) => setNewWsName(e.target.value)}
                placeholder="Ej: Mi nueva empresa"
                className="text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleCreateWorkspace()}
                autoFocus
              />
            </div>
            {nextWsFreeQuota && !nextWsFreeQuota.includesFreeQuota && (
              <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                <p>{noFreeQuotaWarning(nextWsFreeQuota.holderName)}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setNewWsOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleCreateWorkspace} disabled={!newWsName.trim() || creatingWs}>
              {creatingWs ? "Creando..." : "Crear workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WelcomeOverlay visible={tour.showWelcome} onStartTour={tour.startTour} onSkip={tour.skipTour} />
      <GuidedTour active={tour.tourActive} currentStep={tour.currentStep} totalSteps={tour.totalSteps} onNext={tour.nextStep} onSkip={tour.skipTour} />
      <TourCompletion visible={tour.showCompletion} onDismiss={tour.dismissCompletion} />
      {guiaEnabled && <AssistantCopilotWidget />}
      {guiaEnabled && <GlobalGuideUpdatesNotifier onCountChange={setGuideUnreadCount} />}
      {(isCoordinator || hasCoordinator) && <CoordinatorMessageNotifier />}
    </div>
  );
}
